import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { printOne } from '../services/printerService';

/**
 * 🖨️ Druckt mehrere Etiketten für ein Mehrfach-Paket — eines nach dem anderen,
 * mit Bestätigung dazwischen, damit man jeden Bon einzeln abreißen kann
 * (statt 2-3 Etiketten auf einem langen Streifen).
 */
export default function PrintLabelDialog({ paket, copies, colors, onClose }) {
  const { t } = useTranslation();
  const dark = colors?.darkMode;
  const c = {
    bg: dark ? '#23272f' : '#fff',
    text: dark ? '#fff' : '#23272f',
    sub: dark ? '#aaa' : '#666',
    accent: '#1976d2',
  };

  const total = Math.max(1, copies || 1);
  const [printed, setPrinted] = useState(0); // wie viele Etiketten erfolgreich gedruckt
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const startedRef = useRef(false);

  const printCopy = async (i) => {
    setBusy(true);
    setError(null);
    try {
      await printOne(paket, { index: i, total });
      setPrinted(i);
    } catch (e) {
      setError(e?.message || t('printer.printFailed'));
    } finally {
      setBusy(false);
    }
  };

  // Erstes Etikett automatisch drucken, sobald der Dialog erscheint
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    printCopy(1);
    // eslint-disable-next-line
  }, []);

  const allDone = printed >= total && !error;
  const nextIndex = printed + 1;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 3500,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div style={{ background: c.bg, color: c.text, borderRadius: 14, width: '100%', maxWidth: 380, padding: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 6 }}>🖨️</div>
        <strong style={{ fontSize: 16 }}>
          {total > 1 ? t('printer.printMultipleLabels', { total }) : t('printer.printLabelTitle')}
        </strong>

        <div style={{ margin: '16px 0', minHeight: 48, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {busy ? (
            <div style={{ color: c.sub }}>⏳ {t('printer.printingLabelOf', { index: nextIndex, total })}</div>
          ) : error ? (
            <div style={{ color: '#f44336' }}>⚠️ {error}</div>
          ) : allDone ? (
            <div style={{ color: '#4CAF50', fontWeight: 600 }}>✓ {t('printer.allLabelsPrinted', { total })}</div>
          ) : (
            <div style={{ color: c.text }}>
              {t('printer.labelPrintedOf', { printed, total })}<br />
              <span style={{ color: c.sub, fontSize: 14 }}>{t('printer.tearOffThenNext')}</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          {error ? (
            <>
              <button onClick={onClose} style={btn(false, c, dark)}>{t('common.cancel')}</button>
              <button onClick={() => printCopy(nextIndex)} disabled={busy} style={btn(true, c, dark)}>
                🔄 {t('printer.retry')}
              </button>
            </>
          ) : allDone ? (
            <button onClick={onClose} style={btn(true, c, dark)}>{t('common.done')}</button>
          ) : (
            <>
              <button onClick={onClose} disabled={busy} style={btn(false, c, dark)}>{t('common.cancel')}</button>
              <button onClick={() => printCopy(nextIndex)} disabled={busy} style={btn(true, c, dark)}>
                {t('printer.nextLabel', { index: nextIndex, total })}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function btn(primary, c, dark) {
  return {
    flex: 1, padding: '12px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600,
    cursor: 'pointer',
    background: primary ? c.accent : (dark ? '#3a4150' : '#e2e8f0'),
    color: primary ? '#fff' : c.text,
  };
}
